<script>
    import Flatpickr from 'flatpickr';
    import moment from 'moment';

    import { Danish } from 'flatpickr/dist/l10n/da.js';
    import { Swedish } from 'flatpickr/dist/l10n/sv.js';
    import { Finnish } from 'flatpickr/dist/l10n/fi.js';
    import { Norwegian } from 'flatpickr/dist/l10n/no.js';

    switch(moment_locale) {
        case "da": Flatpickr.localize(Danish); break;
        case "sv": Flatpickr.localize(Swedish); break;
        case "fi": Flatpickr.localize(Finnish); break;
        case "no": Flatpickr.localize(Norwegian); break;
    }

    Flatpickr.l10ns.default.firstDayOfWeek = 1;

    export default {
        props: {
            value: {},
            name: {
                required: true
            },
            min_date: {
                default: false
            },
            max_date: {
                default: false
            },
            week_select: {
                default: false
            },
            isRequired: {
                required: false,
                default: false
            },
            clearable: {
                default: false,
            },
            placeholder: {
                required: false,
                default: 'DD-MM-YYYY',
            },
            disableInput: {
                type: Boolean,
                default: false,
            },
            disableWeekends: {
                type: Boolean,
                default: false,
            },
            disableOnDates: {
                type: Array,
                default: () => [],
            },
            format: {
                type: String,
                default: 'DD-MM-YYYY',
            },
            selectFormat: {
                type: String,
                default: 'd-m-Y',
            },
            static: {
                type: Boolean,
                default: false,
            },
            hasError: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                interVal: this.value,
                flatPickr: null,
                selected_year : null,
                selected_week : null,
            };
        },

        computed: {
            week_picker_selected_label() {
                if (this.selected_week && this.selected_year) {
                    return trans('calendar.week_number_title') + " " + this.selected_week + ", " + this.selected_year;
                }

                return '';
            },

            canClear() {
                if (this.week_select) {
                    return this.clearable && this.selected_week && this.selected_year;
                }

                return this.clearable && this.interVal && this.interVal !== '';
            },
        },

        watch: {
            interVal(val) {
                this.$emit('input', val);
            },

            value(val) {
                this.flatPickr.setDate(val, true, this.selectFormat);
                this.interVal = val;
            },

            min_date(val) {
                const minDateMoment = moment(val, this.format);
                const interValMoment = moment(this.interVal, this.format);

                if (minDateMoment) {
                    this.flatPickr.set('minDate', minDateMoment.toDate());

                    // if the date is less than the min date, set the date to the min date
                    if (!interValMoment.isValid() || interValMoment.isAfter(minDateMoment)) return
                    this.interVal = val;
                }
            },
        },

        mounted() {
            if (this.week_select) {
                this.initWeeklyDatePicker();
            } else {
                this.initDatePicker();
            }

            this.setMinDate();
            this.setMaxDate();

	        if (this.interVal) {
				this.flatPickr.defaultDate = this.interVal;
	        }
        },

        beforeUnmount() {
            if (this.flatPickr) {
                this.flatPickr.destroy();
                this.flatPickr = null;
            }
        },

        methods: {
            changeVal() {
                this.$emit('input', this.interVal);
            },

            handleClear() {
                this.flatPickr && this.flatPickr.clear();
                this.interVal = '';
                this.$emit('clear');
            },

            clearHover(fp) {
                let days = fp.days.childNodes;
                for (let i = days.length; i--;) {
                    days[i].classList.remove("inRange");
                }
            },

            onDayHover(event) {
                if (!event.target.classList.contains("flatpickr-day")) return;
                let days = event.target.parentNode.childNodes;
                let dayIndex = event.target.$i;

                let dayIndSeven = dayIndex / 7;
                let weekStartDay = days[7 * Math.floor(dayIndSeven)].dateObj;
                let weekEndDay = days[7 * Math.ceil(dayIndSeven + 0.01) - 1].dateObj;

                for (let i = days.length; i--;) {
                    let date = days[i].dateObj;
                    if (date > weekEndDay || date < weekStartDay) days[i].classList.remove("inRange");else days[i].classList.add("inRange");
                }
            },

            highlightWeek(fp) {
                if (fp.selectedDateElem) {
                    fp.weekStartDay = fp.days.childNodes[7 * Math.floor(fp.selectedDateElem.$i / 7)].dateObj;
                    fp.weekEndDay = fp.days.childNodes[7 * Math.ceil(fp.selectedDateElem.$i / 7 + 0.01) - 1].dateObj;
                }

                let days = fp.days.childNodes;
                for (let i = days.length; i--;) {
                    let date = days[i].dateObj;
                    if (date >= fp.weekStartDay && date <= fp.weekEndDay){
                        days[i].classList.add("week", "selected");
                    }
                }
            },

            initDatePicker() {
                const vm = this;
                this.flatPickr = new Flatpickr(this.$refs.pickrInput, {
                    disable: [
                        date => vm.disableWeekends && (date.getDay() === 0 || date.getDay() === 6),
                        ...this.disableOnDates
                    ],
                    dateFormat: this.selectFormat,
                    allowInput: !vm.disableInput,
                    static: this.static,
                    onClose: (selectedDates, dateStr, fp) => {
                        vm.flatPickr.setDate(fp._input.value);
                        vm.interVal = fp._input.value;
                    },
                    onChange: (selectedDates, dateStr, fp) => {
                        vm.flatPickr.setDate(fp._input.value);
                        vm.interVal = fp._input.value;
                    },
                    parseDate: (date, format) => {
                        const parsedDate = format && format === this.selectFormat ? moment(date, this.format) : moment(date);
                        const isValidDateInput = parsedDate.isValid();

                        if (vm.max_date && parsedDate > moment(vm.max_date, this.format)) {
                            // If the parsed date is bigger than the max we revert a century.
                            //
                            // Note: An example of this would be a DoB field, where the user could type
                            //       the date "01-01-55", resulting in a parsed date of "01-01-2055".
                            //       Here we assume that the person is not born in the future but in 1955.
                            parsedDate.set('year', parsedDate.year() - 100)
                        }

                        const formattedDate = parsedDate.toDate();
                        return isValidDateInput ? formattedDate : '';
                    },
                });
            },

            initWeeklyDatePicker() {
                const vm = this;

                this.flatPickr = new Flatpickr(this.$refs.pickrInput, {
                    mode: "single",
                    enableTime: false,
                    disableMobile: true,
                    dateFormat: vm.week_picker_selected_label,
                    altFormat: "W Y",
                    weekNumbers: true,
                    getWeek: (date) => {
                        return this.$filters.weekNumber(date)
                    },
                    onMonthChange: function(dateObj, dateStr, fp) {
                        return vm.highlightWeek(fp);
                    },
                    onClose: function (dateObj, dateStr, fp) {
                        vm.clearHover(fp);
                    },
                    onReady: [
                        function(dateObj, dateStr, fp) {
                            fp.days.parentNode.addEventListener("mouseover", vm.onDayHover);
                        },
                        function(dateObj, dateStr, fp) {
                            vm.highlightWeek(fp);
                        }
                    ],
                    onChange: [
                        function(dateObj, dateStr, fp) {
                            vm.highlightWeek(fp);
                        },
                        function(dateObj, dateStr, fp) {
                            vm.$nextTick(function(){
                                const weekNumber = fp.selectedDates[0]
                                    ? this.$filters.weekNumber(fp.selectedDates[0])
                                    : 0

                                if (weekNumber >= 54 && dateObj.length > 0) {
                                    // if the week is 54, set the date to the first day the next year
                                    // for flatpickr to correctly pick up week 1 instead of week 53.
                                    let yearNumber = dateObj[0].getFullYear();
                                    fp.setDate(`01-01-${++yearNumber}`, true, this.selectFormat);
                                    return;
                                }

                                vm.$emit('week', weekNumber) ;
                                vm.selected_week = weekNumber;

                                if (dateObj.length > 0) {
                                    const startOfWeek = moment(dateObj[0]).startOf('isoWeek');
                                    let yearNumber = startOfWeek.year();

                                    // Sometimes week 1 starts in December. For example, week 1, 2025, is Dec. 30, 2024
                                    // to Jan. 5, 2025. We (moment) correctly figure out the week number, but when we
                                    // get the year using `startOf('isoWeek')`, we get 2024 instead of 2025. This is a
                                    // bit of a hack, but if we get week number 1, and December, we can add one to the
                                    // year. In this edge-case, using `endOf('isoWeek');` would result in the correct
                                    // year, but that leads to a different edge case when selecting week 53, 2027, for
                                    // example.
                                    if (weekNumber === 1 && startOfWeek.month() === 11) {
                                        ++yearNumber;
                                    }

                                    // We could maybe get rid of this `toString()` call.
                                    //
                                    // We used to get the year using `.format('YYYY')`, and that would return a string.
                                    // We now handle the year as a number in the above code, but I don't want to break
                                    // code that may assume that we always get a string back.
                                    const yearString = yearNumber.toString();

                                    vm.$emit('year', yearString);
                                    vm.selected_year = yearString;
                                }

                                $(vm.$refs.pickrInput).val(vm.week_picker_selected_label)
                            });
                        }
                    ],
                });
            },

            setMinDate() {
                if (this.min_date) {
                    let min_date_moment = moment(this.min_date, this.format);

                    if (min_date_moment) {
                        this.flatPickr.set('minDate', min_date_moment.toDate());
                    }
                }
            },

            setMaxDate() {
                if (this.max_date) {
                    let max_date_moment = moment(this.max_date, this.format);

                    if (max_date_moment) {
                        this.flatPickr.set('maxDate', max_date_moment.toDate());
                    }
                }
            },
        },
    };
</script>

<template>
    <div :class="{ 'input-group': canClear }">
        <input
	        v-if="week_select"
	        v-bind="$attrs"
	        ref="pickrInput"
	        class="form-control m-b-0"
	        autocomplete="off"
	        readonly="readonly"
	        :name="name"
	        type="text"
	        :required="isRequired"
	        :placeholder="placeholder"
            :class="{ 'border-danger': hasError }"
        >
        <input
	        v-else-if="!disableInput"
	        v-bind="$attrs"
	        ref="pickrInput"
	        :value="interVal"
	        class="form-control m-b-0"
	        autocomplete="off"
	        :name="name"
	        type="text"
	        :required="isRequired"
	        :placeholder="placeholder"
            :class="{ 'border-danger': hasError }"
        >
        <input
	        v-else
	        v-bind="$attrs"
	        ref="pickrInput"
	        v-model="interVal"
	        class="form-control m-b-0"
	        autocomplete="off"
	        readonly="readonly"
	        :name="name"
	        type="text"
	        :required="isRequired"
	        :placeholder="placeholder"
            :class="{ 'border-danger': hasError }"
        >
        <span v-if="canClear" class="input-group-btn">
            <button class="btn btn-default" @click.prevent="handleClear">
                <i class="zmdi zmdi-close"></i>
            </button>
        </span>
    </div>
</template>

<style scoped>
    .datepicker-wrapper--full {
        .flatpickr-wrapper {
            width: 100%;
        }
    }
</style>
